import { Editor } from '@tiptap/core';
import CodeIcon from '@mui/icons-material/Code';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import LinkIcon from '@mui/icons-material/Link';
import RedoIcon from '@mui/icons-material/Redo';
import UndoIcon from '@mui/icons-material/Undo';
import { ReactComponent as HeaderOneIcon } from '../../assets/svg/h-1.svg';
import { ReactComponent as HeaderTwoIcon } from '../../assets/svg/h-2.svg';
import { ReactComponent as HeaderThreeIcon } from '../../assets/svg/h-3.svg';
import { ReactComponent as FormatBoldIcon } from '../../assets/svg/bold.svg';
import { ReactComponent as FormatItalicIcon } from '../../assets/svg/italic.svg';

interface IUseEditorToolbar {
  editor: Editor | null;
}

const useEditorToolbar = ({ editor }: IUseEditorToolbar) => {
  const focusInstance = () =>
    editor?.chain().focus();

  const toggleBold = () => {
    focusInstance?.()?.toggleBold().run();
  };

  const toggleItalic = () => {
    focusInstance?.()?.toggleItalic().run();
  };

  const toggleStrike = () => {
    focusInstance?.()?.toggleStrike().run();
  };

  const toggleBulletList = () => {
    focusInstance?.()?.toggleBulletList().run();
  };

  const toggleOrderedList = () => {
    focusInstance?.()?.toggleOrderedList().run();
  };

  const toggleBlockquote = () => {
    focusInstance?.()?.toggleBlockquote().run();
  };

  const toggleCodeBlock = () => {
    focusInstance?.()?.toggleCodeBlock().run();
  };

  const insertHorizontalRule = () => {
    focusInstance?.()?.setHorizontalRule().run();
  };

  const setLink = () => {
    const previousUrl = editor?.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === '') {
      focusInstance?.()?.extendMarkRange('link').unsetLink().run();
      return;
    }

    // update link
    focusInstance?.()?.extendMarkRange('link').setLink({ href: url }).run();
  };

  const undo = () => {
    focusInstance?.()?.undo().run();
  };

  const redo = () => {
    focusInstance?.()?.redo().run();
  };

  const toggleHeadingLevel = (level: 1 | 2 | 3) =>
    () => {
      focusInstance?.()?.toggleHeading({ level }).run();
    };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target?.files?.[0]; // Get the selected file

    if (file) {
      const reader = new FileReader();

      reader.onloadend = () => {
        const imageSrc = reader.result as string;

        if (editor) {
          const { view } = editor ?? {};
          const { state } = view ?? {};
          const { schema } = state ?? {};
          const pos = editor?.state.doc.content.size ?? 0;

          const node = schema?.nodes.image.create({ src: imageSrc });
          const transaction = view?.state.tr.insert(pos ?? 0, node ?? []);

          return view?.dispatch(transaction);
        }

        return null;
      };
      reader.readAsDataURL(file); // Read the file as a data URL
    }
  };

  const toolbarConfig = [
    {
      key: 1,
      icon: HeaderOneIcon,
      title: 'Heading 1',
      handleClick: toggleHeadingLevel(1),
      isActive: editor?.isActive('heading', { level: 1 }),
    },
    {
      key: 2,
      icon: HeaderTwoIcon,
      title: 'Heading 2',
      handleClick: toggleHeadingLevel(2),
      isActive: editor?.isActive('heading', { level: 2 }),
    },
    {
      key: 3,
      icon: HeaderThreeIcon,
      title: 'Heading 3',
      handleClick: toggleHeadingLevel(3),
      isActive: editor?.isActive('heading', { level: 3 }),
    },
    {
      key: 4,
      icon: FormatBoldIcon,
      title: 'Bold',
      handleClick: toggleBold,
      isActive: editor?.isActive('bold'),
    },
    {
      key: 5,
      icon: FormatItalicIcon,
      title: 'Italic',
      handleClick: toggleItalic,
      isActive: editor?.isActive('italic'),
    },
    {
      key: 6,
      icon: FormatStrikethroughIcon,
      title: 'Strikethrough',
      handleClick: toggleStrike,
      isActive: editor?.isActive('strike'),
    },
    {
      key: 7,
      icon: FormatListBulletedIcon,
      title: 'Bullet list',
      handleClick: toggleBulletList,
      isActive: editor?.isActive('bulletList'),
    },
    {
      key: 8,
      icon: FormatListNumberedIcon,
      title: 'Numbered list',
      handleClick: toggleOrderedList,
      isActive: editor?.isActive('orderedList'),
    },
    {
      key: 9,
      icon: FormatQuoteIcon,
      title: 'Quote',
      handleClick: toggleBlockquote,
      isActive: editor?.isActive('blockquote'),
    },
    {
      key: 10,
      icon: CodeIcon,
      title: 'Code block',
      handleClick: toggleCodeBlock,
      isActive: editor?.isActive('codeBlock'),
    },
    {
      key: 14,
      icon: LinkIcon,
      title: 'Link',
      handleClick: setLink,
      isActive: editor?.isActive('link'),
    },
    {
      key: 11,
      icon: HorizontalRuleIcon,
      title: 'Divider',
      handleClick: insertHorizontalRule,
      isActive: false,
    },
    {
      key: 12,
      icon: UndoIcon,
      title: 'Undo',
      handleClick: undo,
      isActive: false,
    },
    {
      key: 13,
      icon: RedoIcon,
      title: 'Redo',
      handleClick: redo,
      isActive: false,
    },
  ];

  return { toolbarConfig, handleFileUpload };
};

export default useEditorToolbar;
